import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  TextField,
  Avatar,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';


function Profile() {
  const { token, user, isAuthenticated, signIn } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [usage, setUsage] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileUser, setProfileUser] = useState(user);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    photo: null,
    photoPreview: null
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/users/profile');
        setProfile(response.data);
        setUsage(response.data.usage);
        setEditFormData({
          name: response.data.name || '',
          photo: null,
          photoPreview: response.data.photo || null
        });
        // Set user data from profile response if not already set
        if (!profileUser) {
          setProfileUser({
            email: response.data.email,
            id: response.data._id || response.data.id
          });
        }
      } catch (err) {
        setError('Failed to load profile');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (token && isAuthenticated) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [token, isAuthenticated]);

  const handleEditClick = () => {
    setIsEditing(true);
    setEditFormData({
      name: profile?.name || '',
      photo: null,
      photoPreview: profile?.photo || null
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError(null);
    setSuccess(null);
    setEditFormData({
      name: profile?.name || '',
      photo: null,
      photoPreview: profile?.photo || null
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Photo size must be less than 2MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditFormData({
          ...editFormData,
          photo: file,
          photoPreview: reader.result
        });
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('name', editFormData.name);
      if (editFormData.photo) {
        formData.append('photo', editFormData.photo);
      }

      const response = await api.patch('/users/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setProfile(response.data);
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      
      // Update auth context with updated user data
      signIn({ 
        ...user, 
        name: response.data.name, 
        photo: response.data.photo,
        email: response.data.email,
        id: response.data._id || response.data.id
      }, token);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!isAuthenticated || !token) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <Alert severity="warning">You must be signed in to view your profile.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Profile Information
                </Typography>
                {!isEditing && (
                  <Button
                    startIcon={<EditIcon />}
                    onClick={handleEditClick}
                    variant="outlined"
                    size="small"
                  >
                    Edit Profile
                  </Button>
                )}
              </Box>

              {isEditing ? (
                <Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      src={editFormData.photoPreview}
                      sx={{ width: 120, height: 120, mb: 2 }}
                    >
                      {editFormData.name?.charAt(0)?.toUpperCase() || profile?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="photo-upload"
                      type="file"
                      onChange={handlePhotoChange}
                    />
                    <label htmlFor="photo-upload">
                      <IconButton color="primary" component="span">
                        <PhotoCameraIcon />
                      </IconButton>
                    </label>
                    <Typography variant="caption" color="text.secondary">
                      Click to change photo (max 2MB)
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    label="Username"
                    name="name"
                    value={editFormData.name}
                    onChange={handleInputChange}
                    margin="normal"
                    placeholder="Enter your username"
                    helperText="This will be displayed on your blog posts"
                  />

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                    Email: <strong>{profileUser?.email || profile?.email || 'Loading...'}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Email cannot be changed
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      startIcon={<SaveIcon />}
                      onClick={handleSaveProfile}
                      variant="contained"
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      startIcon={<CancelIcon />}
                      onClick={handleCancelEdit}
                      variant="outlined"
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      src={profile?.photo}
                      sx={{ width: 120, height: 120, mb: 2 }}
                    >
                      {profile?.name?.charAt(0)?.toUpperCase() || profile?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                    {profile?.name && (
                      <Typography variant="h6">
                        {profile.name}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body1" gutterBottom>
                    Email: <strong>{profileUser?.email || profile?.email || 'Loading...'}</strong>
                  </Typography>
                  {profile?.name && (
                    <Typography variant="body1" gutterBottom>
                      Username: <strong>{profile.name}</strong>
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Usage Statistics
              </Typography>
              {usage && (
                <>
                  <Typography variant="body1" gutterBottom>
                    Generation Count: <strong>{usage.generationCount}</strong>
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Last Reset: <strong>{new Date(usage.lastResetDate).toLocaleDateString()}</strong>
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>


      </Grid>
    </Container>
  );
}

export default Profile; 